import { MultiDatePicker } from 'components/MyDatePicker/MultiDatePicker';
import moment from 'moment-jalaali';
import React from 'react';
import { localPhoneNumberPattern } from 'store/entity/patterns';
import { timeInMinutes } from 'utils/date';
import {
  convertNumbersToProperFormat,
  englishNumber,
  isANumber,
} from 'utils/number';

import Input from 'components/Input';
import { QuestionBody, QuestionWrapper } from './styles';
import TitleInput from './TitleInput';

// import ClassicEditor from '@rastech-inc/ckeditor';
import PigeonMaps from 'components/PigeonMaps';
import { connect, MapStateToProps } from 'react-redux';
import { getAccessToken } from 'api/Utils';
import CKEditorField, {
  CkEditorBaseConfig,
} from '../CKEditorField/CKEditorField';
import Textarea from '../Textarea/Textarea';
import DescriptionInput, {
  getDescriptionInputStatus,
} from './DescriptionInput';
import { checkINID } from './ResponseForm/ResponseForm';
import { Error } from './styles';
import { getPureTextFromHtml } from 'store/tasks/taskManipulate';
import {
  ISurveyView,
  IAnswerView,
  IQuestionsView,
  ITextAnswerView,
  ITextQuestionView,
} from 'store/survey/surveyTypes';
import { ITags } from 'store/providers/providerTypes';
import { RootState } from 'store/store';
import { Duration } from '../Duration/Duration';
import i18n from '../../i18n';
import messages from './Messages';
import PageCKEditor from '../FileManager/ItemsViewer/Quill/PageCkEditor';

interface IOwnProps {
  answer?: ITextAnswerView;
  isRequired: boolean;
  disabled?: boolean;
  onChange: (newAnswer: any, questionId: number) => void;
  question: ITextQuestionView;
  onShowDataSourceModal: (dataSource: string) => void;
  token: string;
  tags?: ITags['data'];
  survey: ISurveyView;
  dataSourceValues: { [key: string]: string };
  answers?: { [key: number]: IAnswerView };
  questions?: { [questionId: number]: IQuestionsView };
  getNewsComplementaryContext?: () => string;
  aiGptModel?: string;
}

interface IStateToProps {
  token: string;
}
type ITextQuestionProps = IOwnProps & IStateToProps;

interface ITextQuestionState {
  addDescription: boolean;
}

const inputPatterns: {
  [type in ITextQuestionView['inputType']]?: string;
} = {
  text: undefined,
  date: undefined,
  phone: localPhoneNumberPattern,
  number: '[0-9]*',
  digits: '[0-9]*',
};

const inputTypes: {
  [type in Exclude<
    ITextQuestionView['inputType'],
    'date' | 'area' | 'duration' | 'dateAndTime'
  >]: 'tel' | 'text';
} = {
  text: 'text',
  map: 'text',
  phone: 'tel',
  number: 'tel',
  digits: 'tel',
  inid: 'tel',
  rich: 'text',
  none: 'text',
};

const inputModes: {
  [mode in ITextQuestionView['inputType']]: 'tel' | 'text' | 'numeric';
} = {
  text: 'text',
  date: 'text',
  duration: 'text',
  dateAndTime: 'text',
  map: 'text',
  phone: 'tel',
  number: 'numeric',
  digits: 'numeric',
  area: 'text',
  inid: 'numeric',
  rich: 'text',
  none: 'text',
};

class TextQuestion extends React.Component<
  ITextQuestionProps,
  ITextQuestionState
> {
  constructor(props: ITextQuestionProps) {
    super(props);
    this.state = {
      addDescription:
        !props.answer || (props.answer && !props.answer.description),
    };
  }

  descriptionHandler = () => {
    this.setState({ addDescription: !this.state.addDescription });
  };

  handleChange = (value: any, name: string) => {
    const newAnswer = { ...this.props.answer, [name]: value };
    this.props.onChange(newAnswer, this.props.question.id);
  };

  setDate = (_type: string, value: string | number | null) => {
    this.handleChange(value ? Number(value).toString() : null, 'value');
  };

  setDuration = (_type: string, value: string | number) => {
    this.handleChange(value ? value.toString() : null, 'value');
  };

  setLocation = (location: number[] | null) => {
    this.handleChange(location ? location.toString() : null, 'value');
  };

  setTime = (value: moment.Moment) => {
    const minutes = timeInMinutes(value.valueOf());
    this.handleChange(minutes.toString(), 'value');
  };

  preventWhiteSpace = (e: React.KeyboardEvent) => {
    if (e.which === 32 || e.key === 'Enter') {
      e.preventDefault();
      return false;
    }
    return;
  };
  preventSubmitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      return false;
    }
    return;
  };

  shouldComponentUpdate(
    nextProps: Readonly<ITextQuestionProps>,
    nextState: Readonly<ITextQuestionState>
  ): boolean {
    return (
      nextProps.answer !== this.props.answer ||
      nextProps.question !== this.props.question ||
      nextProps.isRequired !== this.props.isRequired ||
      nextProps.answers !== this.props.answers ||
      nextState !== this.state
    );
  }

  render() {
    const { question, answer, isRequired, disabled, token, tags, survey } =
      this.props;

    const inputType: ITextQuestionView['inputType'] = question.inputType;

    const questionMaxLength: ITextQuestionView['maxLength'] =
      question.maxLength;

    const maxLength =
      questionMaxLength > 0 && inputType !== 'phone'
        ? questionMaxLength
        : inputType === 'inid'
          ? 10
          : undefined;

    let remainingCharacters: number | undefined;
    if (maxLength && answer && inputType !== 'inid') {
      const difference = maxLength - (answer?.value?.length ?? 0);
      if (difference < 4) {
        remainingCharacters = difference;
      }
    }
    const relatedDataSource = question.fields?.find(
      (it) => it.name === 'conf_dataSource'
    )?.value;
    const isOnlyNumber =
      inputType === 'number' ||
      inputType === 'digits' ||
      inputType === 'phone' ||
      inputType === 'inid';
    const remainingLabel = isANumber(remainingCharacters)
      ? messages.remainingCharacters(
          convertNumbersToProperFormat(remainingCharacters!)
        )
      : undefined;

    const descriptionStatus = survey
      ? getDescriptionInputStatus(survey, question)
      : true;

    if (inputType === 'none') {
      return (
        <QuestionWrapper disabled={disabled} style={{ padding: '1rem' }}>
          <PageCKEditor
            editing={false}
            updateContent={() => {
              /*do nothing*/
            }}
            saving={false}
            content={question.hint}
          />
        </QuestionWrapper>
      );
    }

    return (
      <QuestionWrapper disabled={disabled}>
        <TitleInput
          fields={question.fields}
          hint={question.hint}
          required={question.answerRequired}
          title={question.title}
          dataSourceName={disabled ? undefined : relatedDataSource}
          onShowDataSourceModal={this.props.onShowDataSourceModal}
          tags={question.tags}
          allTags={tags}
          indexNumber={question.indexOrder}
          survey={survey}
          answers={this.props.answers}
          questions={this.props.questions}
          getNewsComplementaryContext={this.props.getNewsComplementaryContext}
          aiGptModel={this.props.aiGptModel}
          onNewsGenerated={(content) => {
            const value = inputType === 'rich' ? `<p>${content}</p>` : content;
            this.handleChange(value, 'value');
          }}
        />
        <QuestionBody>
          {['date', 'dateAndTime'].includes(inputType) ? (
            <div>
              <MultiDatePicker
                id={question.id.toString()}
                setDate={(value) => this.setDate(`${question.id}`, value)}
                hasTime={inputType === 'dateAndTime'}
                value={Number(answer?.value) ?? null}
              />
              <div className="relative">
                <Input
                  id={`${Math.random()}`}
                  type="text"
                  required={this.props.isRequired}
                  value={Number(answer?.value) || undefined}
                  className="absolute"
                  style={{ zIndex: -1, top: '-3rem' }}
                />
              </div>
            </div>
          ) : inputType === 'duration' ? (
            <div>
              <Duration
                id={question.id.toString()}
                setDuration={(duration: string) =>
                  this.setDuration(`${question.id}`, duration)
                }
                value={Number(answer?.value) ?? 0}
                isRequired={isRequired}
              />
            </div>
          ) : inputType === 'area' ? (
            <>
              <Textarea
                id={question.id.toString()}
                value={(answer && answer.value) || ''}
                required={isRequired}
                maxLength={maxLength}
                placeholder={getPureTextFromHtml(question.hint) || undefined}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  const value = e.target.value;
                  this.handleChange(value, 'value');
                }}
              />

              {remainingLabel && <div>{remainingLabel}</div>}
            </>
          ) : inputType === 'rich' ? (
            <div>
              <CKEditorField
                id={'ckeditor' + question.id}
                disabled={disabled}
                style={{}}
                // editor={ClassicEditor}
                data={(answer && answer.value) || ''}
                onChange={(_: any, editor: any) => {
                  const data = editor.getData();
                  this.handleChange(data, 'value');
                }}
                dir="rtl"
                config={CkEditorBaseConfig(token)}
                wrapperClassName={'ckeditorMinHeight200'}
              />
              <div className="relative">
                <Input
                  id={`${Math.random()}`}
                  type="text"
                  required={this.props.isRequired}
                  value={
                    (answer &&
                      answer.value &&
                      answer.value.length > 0 &&
                      answer.value) ||
                    undefined
                  }
                  className="absolute"
                  style={{ zIndex: -1, top: '-3rem' }}
                />
              </div>
            </div>
          ) : inputType === 'map' ? (
            <PigeonMaps
              location={
                answer && answer.value
                  ? answer.value.split(',').map(Number)
                  : null
              }
              onChangeLocation={this.setLocation}
            />
          ) : (
            <>
              <Input
                id={question.id.toString()}
                type={inputTypes[inputType]}
                name={question.id.toString()}
                value={(answer && answer.value) || ''}
                pattern={inputPatterns[inputType]}
                required={isRequired}
                inputMode={inputModes[inputType]}
                className="form-input"
                maxLength={maxLength}
                onKeyPress={
                  isOnlyNumber
                    ? this.preventWhiteSpace
                    : this.preventSubmitOnEnter
                }
                placeholder={getPureTextFromHtml(question.hint) || undefined}
                autoComplete="off"
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (inputType === 'inid') {
                    if (value && !checkINID(value)) {
                      this.handleChange(
                        {
                          message: i18n.t('invalidINID', {
                            ns: 'survey',
                          }) as string,
                        },
                        'error'
                      );
                    } else {
                      this.handleChange(undefined, 'error');
                    }
                  }
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (isOnlyNumber) {
                    const englishDigits = englishNumber(value);
                    if (!isNaN(Number(englishDigits))) {
                      this.handleChange(englishDigits, 'value');
                    }
                  } else {
                    this.handleChange(value, 'value');
                  }
                }}
              />
              {remainingLabel && <div>{remainingLabel}</div>}
              {answer && answer.error && <Error> {answer.error.message}</Error>}
            </>
          )}
          {descriptionStatus && inputType !== 'rich' && (
            <DescriptionInput
              question={question}
              survey={survey}
              open={!this.state.addDescription}
              handler={this.descriptionHandler}
              id={question.id}
              text={answer && answer.description}
              onChangeInput={this.handleChange}
              disabled={disabled}
            />
          )}
        </QuestionBody>
      </QuestionWrapper>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state
) => {
  const token = getAccessToken(state)!;
  return { token };
};
export default connect(mapStateToProps)(TextQuestion);
